import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { ROOTS, paths } from 'src/routes/paths';
import { CONFIG } from 'src/config-global';
import { POLICY } from 'src/utils/constants';
import { hasIncidentFeatures } from 'src/utils/helper';
import { navData } from 'src/layouts/config-nav-dashboard';
import { DashboardLayout } from 'src/layouts/dashboard';
import { LoadingScreen } from 'src/components/loading-screen';
import { AuthGuard } from 'src/auth/guard';
import AccessGuard from '../../auth/guard/access-guard';

const layoutContent = (
  <DashboardLayout data={{ nav: navData }}>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

const JACKPOTS_ROOT = paths.dashboard.jackpotConfiguration.root;

const Loadable =
  (Component) =>
  ({ policies, ...props }) => (
    <Suspense fallback={<LoadingScreen />}>
      <AccessGuard policies={policies}>
        <Component {...props} />
      </AccessGuard>
    </Suspense>
  );

const IndexPage = lazy(() => import('src/pages/dashboard'));
const GamesConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-configuration/list'))
);
const EnginesManagementListPage = Loadable(lazy(() => import('src/pages/dashboard/engines/list')));

const ExternalGamesConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-configuration-external/list'))
);

const BonusBuyConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/feature-configuration/bonus-buy/list'))
);
const AnteBetConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/feature-configuration/ante-bet/list'))
);

const OperatorsListPage = Loadable(lazy(() => import('src/pages/dashboard/operators/list')));
const OperatorDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/operators/details/details'))
);

const GamesConfigurationDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-configuration/details/details'))
);

const ClientsConfigurationListPage = Loadable(
  lazy(() => import('src/pages/dashboard/client-configuration/list'))
);

const ClientsConfigurationDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/client-configuration/details/details'))
);
const UserListPage = Loadable(lazy(() => import('src/pages/dashboard/user/list')));
const RolesListPage = Loadable(lazy(() => import('src/pages/dashboard/roles/list')));

const JackpotsPage = Loadable(lazy(() => import('src/pages/dashboard/jackpot-configuration/list')));
const ProgressiveJackpotsDetailsPage = Loadable(
  lazy(
    () => import('src/pages/dashboard/jackpot-configuration/progressive-jackpots/details/details')
  )
);
const GroupDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/jackpot-configuration/groups/details/details'))
);
const LoaderPage = Loadable(lazy(() => import('src/pages/dashboard/loader/loader')));

const OsListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-history/open-sessions/list'))
);

const GameRoundHistoryListPage = Loadable(
  lazy(() => import('src/pages/dashboard/game-history/game-round-history/list'))
);

const HistoryManager2Page = Loadable(
  lazy(() => import('src/pages/dashboard/game-history/history-manager-2/list'))
);

const RtpVerificationPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/rtpVerification/list'))
);


const CallbackToolPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/callbackTool/list'))
);

const GameEventLoggerPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/gameEventLogger/list'))
);

const IncidentsPage = Loadable(lazy(() => import('src/pages/dashboard/incidents/list')));

const TestFormBuilderPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/formBuilder/list'))
);

const RtpVerificationDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/rtpVerification/details/details'))
);

const LoadTestingPage = Loadable(lazy(() => import('src/pages/dashboard/testing/load/list')));

const LoadTestingDetailsPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/load/details/details'))
);

const GameSimulationPage = Loadable(
  lazy(() => import('src/pages/dashboard/testing/gameSimulation/list'))
);

const BonusesListPage = Loadable(
  lazy(() => import('src/pages/dashboard/bonus-configuration/bonuses/list'))
);
const UserBonusesListPage = Loadable(
  lazy(() => import('src/pages/dashboard/bonus-configuration/user-bonuses/list'))
);

const SelectPage = Loadable(
  lazy(() => import('src/pages/dashboard/select-configuration/select-page'))
);

const DictionariesPage = Loadable(
  lazy(() => import('src/pages/dashboard/dictionaries-configuration/list'))
);

const AuditListPage = Loadable(lazy(() => import('src/pages/dashboard/audit/list')));

export const dashboardRoutes = [
  {
    path: ROOTS.DASHBOARD,
    element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'user',
        children: [
          { element: <UserListPage policies={[POLICY.USERS_GET]} />, index: true },
          { path: 'list', element: <UserListPage policies={[POLICY.USERS_GET]} /> },
          { path: 'roles', element: <RolesListPage policies={[POLICY.ROLE_GET]} /> },
        ],
      },
      {
        path: 'games-configuration',
        children: [
          {
            element: <GamesConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
            index: true,
          },
          {
            path: 'list',
            element: <GamesConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
          },
          {
            path: 'external',
            element: <ExternalGamesConfigurationListPage policies={[POLICY.GAME_CONF_GET_EXT]} />,
          },
          {
            path: ':id',
            element: <GamesConfigurationDetailsPage policies={[POLICY.GAME_CONF_GET]} />,
          },
        ],
      },
      {
        path: 'feature-configuration',
        children: [
          {
            element: <BonusBuyConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
            index: true,
          },
          {
            path: 'bonus-buy',
            element: <BonusBuyConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
          },
          {
            path: 'ante-bet',
            element: <AnteBetConfigurationListPage policies={[POLICY.GAME_CONF_GET]} />,
          },
          // {
          //   path: ':id',
          //   element: <GamesConfigurationDetailsPage policies={[POLICY.GAME_CONF_CREATE_UPDATE]} />,
          // },
        ],
      },
      {
        path: 'engine-management',
        children: [
          {
            element: <EnginesManagementListPage policies={[POLICY.ENGINES_GET]} />,
            index: true,
          },
          {
            path: 'list',
            element: <EnginesManagementListPage policies={[POLICY.ENGINES_GET]} />,
          },
        ],
      },
      {
        path: 'client-configuration',
        children: [
          {
            element: <ClientsConfigurationListPage policies={[POLICY.CLIENT_CONF_GET]} />,
            index: true,
          },
          {
            path: 'list',
            element: <ClientsConfigurationListPage policies={[POLICY.CLIENT_CONF_GET]} />,
          },
          {
            path: ':id',
            element: <ClientsConfigurationDetailsPage policies={[POLICY.CLIENT_CONF_GET]} />,
          },
          {
            path: 'loader',
            element: <LoaderPage policies={[POLICY.PACKAGES_GET]} />,
          },
        ],
      },
      {
        path: 'select-page-configuration',
        children: [
          {
            element: <SelectPage policies={[POLICY.SELECT_PAGE_GET]} />,
            index: true,
          },
        ],
      },
      {
        path: 'jackpot-configuration',
        children: [
          // Pools, jackpots and groups are one page now. Its three read policies are all listed
          // because `hasUserAccess` is any-of — holding one of them is enough to enter, and the page
          // then shows only the sections that policy covers.
          {
            index: true,
            element: (
              <JackpotsPage
                policies={[POLICY.JACKPOT_GET, POLICY.JACKPOT_GROUP_GET, POLICY.LIQUIDITY_POOL_GET]}
              />
            ),
          },
          // The three old list paths keep resolving so bookmarks and any link still pointing at
          // them land on the composite page rather than a blank route.
          { path: 'list', element: <Navigate to={JACKPOTS_ROOT} replace /> },
          {
            path: 'liquidity-pools',
            children: [
              { index: true, element: <Navigate to={JACKPOTS_ROOT} replace /> },
              { path: 'list', element: <Navigate to={JACKPOTS_ROOT} replace /> },
            ],
          },
          {
            path: ':id',
            element: <ProgressiveJackpotsDetailsPage policies={[POLICY.JACKPOT_GET]} />,
          },
          {
            path: 'groups',
            children: [
              { index: true, element: <Navigate to={JACKPOTS_ROOT} replace /> },
              { path: 'list', element: <Navigate to={JACKPOTS_ROOT} replace /> },
              {
                path: ':id',
                element: <GroupDetailsPage policies={[POLICY.JACKPOT_GROUP_GET]} />,
              },
            ],
          },
        ],
      },
      {
        path: 'game-history',
        children: [
          {
            path: 'opened-sessions',
            children: [
              {
                element: <OsListPage policies={[POLICY.STUCK_GAME_SESSION_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <OsListPage policies={[POLICY.STUCK_GAME_SESSION_GET]} />,
              },
            ],
          },
          {
            path: 'game-round-history',
            children: [
              {
                element: <GameRoundHistoryListPage policies={[POLICY.HISTORY_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <GameRoundHistoryListPage policies={[POLICY.HISTORY_GET]} />,
              },
            ],
          },
        ],
      },
      {
        path: 'investigation',
        children: [
          { element: <HistoryManager2Page policies={[POLICY.HISTORY_GET]} />, index: true },
          { path: 'list', element: <HistoryManager2Page policies={[POLICY.HISTORY_GET]} /> },
        ],
      },
      ...(hasIncidentFeatures()
        ? [
            {
              path: 'incidents',
              children: [
                {
                  element: (
                    <IncidentsPage
                      policies={[POLICY.INCIDENT_GET, POLICY.INCIDENT_CREATE_UPDATE]}
                    />
                  ),
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <IncidentsPage
                      policies={[POLICY.INCIDENT_GET, POLICY.INCIDENT_CREATE_UPDATE]}
                    />
                  ),
                },
              ],
            },
          ]
        : []),
      {
        path: 'testing',
        children: [
          {
            path: 'rtp-verification',
            children: [
              {
                element: <RtpVerificationPage policies={[POLICY.RTP_VERIFICATION_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <RtpVerificationPage policies={[POLICY.RTP_VERIFICATION_GET]} />,
              },
              {
                path: ':id',
                element: <RtpVerificationDetailsPage policies={[POLICY.RTP_VERIFICATION_GET]} />,
              },
            ],
          },
          {
            path: 'form-builder',
            children: [
              {
                element: <TestFormBuilderPage policies={[POLICY.RTP_VERIFICATION_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <TestFormBuilderPage policies={[POLICY.RTP_VERIFICATION_GET]} />,
              },
            ],
          },
          {
            path: 'load',
            children: [
              {
                element: <LoadTestingPage policies={[POLICY.GAME_ENGINE_LOAD_TEST_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <LoadTestingPage policies={[POLICY.GAME_ENGINE_LOAD_TEST_GET]} />,
              },
              {
                path: ':id',
                element: <LoadTestingDetailsPage policies={[POLICY.GAME_ENGINE_LOAD_TEST_GET]} />,
              },
            ],
          },
          {
            path: 'game-simulation',
            children: [
              {
                element: <GameSimulationPage policies={[POLICY.GAME_SIMULATION_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <GameSimulationPage policies={[POLICY.GAME_SIMULATION_GET]} />,
              },
            ],
          },
          {
            path: 'callback-tool',
            children: [
              {
                element: (
                  <CallbackToolPage
                    policies={[POLICY.PROVIDER_INTEGRATION_CALLBACK_CREATE_UPDATE]}
                  />
                ),
                index: true,
              },
            ],
          },
          {
            path: 'game-event-logger',
            children: [
              {
                element: <GameEventLoggerPage policies={[POLICY.GAME_EVENT_LOGGER_GET]} />,
                index: true,
              },
              {
                path: 'list',
                element: <GameEventLoggerPage policies={[POLICY.GAME_EVENT_LOGGER_GET]} />,
              },
            ],
          },
        ],
      },

      // {
      //   path: 'gm/binaries',
      //   children: [
      //     { element: <BinariesListPage />, index: true },
      //     { path: 'list', element: <BinariesListPage /> },
      //   ],
      // },
      // {
      //   path: 'gm/engines',
      //   children: [
      //     { element: <EnginesListPage />, index: true },
      //     { path: 'list', element: <EnginesListPage /> },
      //   ],
      // },
      {
        path: 'operators',
        children: [
          { element: <OperatorsListPage policies={[POLICY.OPERATOR_ADMIN]} />, index: true },
          { path: 'list', element: <OperatorsListPage policies={[POLICY.OPERATOR_ADMIN]} /> },
          {
            path: ':id',
            element: <OperatorDetailsPage policies={[POLICY.OPERATOR_ADMIN]} />,
          },
        ],
      },
      {
        path: 'bonuses',
        children: [
          { element: <BonusesListPage policies={[POLICY.GAME_BONUS_GET]} />, index: true },
          { path: 'list', element: <BonusesListPage policies={[POLICY.GAME_BONUS_GET]} /> },
          { path: 'user', element: <UserBonusesListPage policies={[POLICY.GAME_BONUS_GET]} /> },
        ],
      },
      {
        path: 'dictionaries',
        children: [{ element: <DictionariesPage policies={[POLICY.DICTIONARIES_GET]} />, index: true }],
      },
      {
        path: 'audit',
        children: [
          { element: <AuditListPage policies={[POLICY.AUDIT_GET]} />, index: true },
          { path: 'list', element: <AuditListPage policies={[POLICY.AUDIT_GET]} /> },
        ],
      },
    ],
  },
];
